import React from "react";
import api from "../services/axios";
import { Permission } from "./permissions";
import { getEmailFromJwt } from "../utils/jwtUtils";

/**
 * As permissões do usuário logado, resolvidas pelo servidor a cada sessão.
 *
 * **Por que não vêm no token.** O JWT do portal dura 3 h e não tem refresh. Se a permissão viajasse
 * dentro dele, revogar um acesso levaria até 3 horas para valer — e a tela de perfis prometeria
 * uma coisa que só acontece depois do almoço. Vindo de `GET /api/me/permissions`, o efeito é
 * imediato: o backend invalida o cache dele ao salvar um perfil, e o front revalida a cada carga.
 *
 * **Por que há cache em localStorage.** A resposta é assíncrona e a sidebar renderiza antes dela.
 * Sem cache, todo F5 mostraria um menu vazio piscando. Renderiza-se o último conjunto conhecido e
 * revalida-se em seguida (stale-while-revalidate). O risco é cosmético e limitado a alguns
 * milissegundos: quem manda no acesso é o backend, e uma tela desenhada a mais devolve 403.
 */
interface PermissionState {
  /** Tem a permissão. Use isto — é o gate padrão. */
  can: (permission: Permission) => boolean;
  /** Tem ao menos uma das permissões. */
  canAny: (...permissions: Permission[]) => boolean;
  permissions: ReadonlySet<string>;
  /** Nomes dos perfis do usuário — para exibição, nunca para decidir acesso. */
  profiles: string[];
  /** `true` enquanto a primeira resposta do servidor não chegou (com ou sem cache). */
  loading: boolean;
  /** Rebusca do servidor. Chamado após salvar perfis, para o próprio admin ver o efeito. */
  refresh: () => Promise<void>;
}

const CACHE_PREFIX = "ninaPermissions:";

const PermissionContext = React.createContext<PermissionState | null>(null);

const cacheKeyFor = (email: string | null) => (email ? `${CACHE_PREFIX}${email}` : null);

/** Lê o cache do usuário ATUAL. A chave inclui o e-mail para que trocar de login não herde acesso. */
const readCache = (key: string | null): { permissions: string[]; profiles: string[] } | null => {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const email = getEmailFromJwt(localStorage.getItem("jwtToken") ?? "");
  const cacheKey = cacheKeyFor(email);

  const [state, setState] = React.useState(() => {
    const cached = readCache(cacheKey);
    return {
      permissions: new Set<string>(cached?.permissions ?? []),
      profiles: cached?.profiles ?? [],
    };
  });
  const [loading, setLoading] = React.useState(true);
  const [failed, setFailed] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/me/permissions");
      const permissions: string[] = data?.permissions ?? [];
      const profiles: string[] = data?.profiles ?? [];
      setState({ permissions: new Set(permissions), profiles });
      setFailed(false);
      if (cacheKey) localStorage.setItem(cacheKey, JSON.stringify({ permissions, profiles }));
    } catch (error) {
      /*
        ⚠ "Não consegui perguntar" NÃO é "não tem permissão nenhuma".

        Tratar os dois como a mesma coisa foi o que transformou um 404 de URL num portal
        completamente vazio, sem um único erro na tela — todo mundo logado, e nada para clicar.
        Um conjunto vazio é uma resposta legítima do servidor (usuário realmente sem acesso);
        uma falha na chamada é ausência de resposta, e precisa aparecer como tal.

        Havendo cache, segue-se com ele e a falha é só um retry adiado. Sem cache, a tela abaixo
        diz o que houve em vez de fingir um portal.
      */
      console.error("Não foi possível carregar as permissões do usuário.", error);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [cacheKey]);

  React.useEffect(() => {
    load();
  }, [load]);

  const value = React.useMemo<PermissionState>(
    () => ({
      permissions: state.permissions,
      profiles: state.profiles,
      loading,
      can: (permission) => state.permissions.has(permission),
      canAny: (...permissions) => permissions.some((p) => state.permissions.has(p)),
      refresh: load,
    }),
    [state, loading, load]
  );

  /*
    ⚠ Não renderiza o portal enquanto não souber NADA sobre o usuário.

    O layout decide o destino do fallback de rota por permissão. Com o conjunto vazio,
    `homeRouteFor` responde "portal do cliente" — então um usuário interno que abrisse um link
    direto sem cache seria jogado no portal do cliente antes da resposta chegar, e ficaria lá.
    Roteamento por permissão não pode acontecer antes de haver permissão.

    Isto só aparece quando não há cache (primeiro acesso no navegador, ou logout). O login
    preenche o cache antes de navegar, então o caminho normal nunca passa por aqui.
  */
  if (loading && state.permissions.size === 0) {
    return (
      <div className="flex h-[100dvh] w-full items-center justify-center">
        <span className="text-sm text-gray-500 dark:text-gray-400">Carregando seu acesso…</span>
      </div>
    );
  }

  // A chamada falhou e não há cache: dizer o que houve é melhor que entregar um portal vazio,
  // que o usuário lê como "perdi meus acessos" em vez de "o servidor não respondeu".
  if (failed && state.permissions.size === 0) {
    return (
      <div className="flex h-[100dvh] w-full flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-base font-semibold text-navy-700 dark:text-white">
          Não foi possível carregar seus acessos
        </p>
        <p className="max-w-md text-sm text-gray-600 dark:text-gray-400">
          O servidor não respondeu à consulta de permissões. Seus acessos continuam intactos — é a
          consulta que falhou.
        </p>
        <button
          type="button"
          onClick={() => {
            setLoading(true);
            load();
          }}
          className="rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-semibold text-white shadow-dv-glow"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermissions = (): PermissionState => {
  const context = React.useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions precisa estar dentro de <PermissionProvider>.");
  }
  return context;
};

/**
 * Busca e grava as permissões logo após o login, antes de navegar.
 *
 * Serve a dois propósitos de uma vez: decide para onde mandar o usuário (quem não tem
 * `dashboard.view` não tem o que fazer na home) e deixa o cache quente, para que a sidebar já
 * renderize completa na primeira tela em vez de aparecer item por item.
 *
 * Falha de rede devolve lista vazia: o destino cai no portal do cliente e o provider tenta de
 * novo assim que o layout monta. Um login que funciona pela metade é melhor que um que não acontece.
 */
export const primePermissions = async (jwt: string): Promise<string[]> => {
  try {
    const { data } = await api.get("/me/permissions", {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    const permissions: string[] = data?.permissions ?? [];
    const key = cacheKeyFor(getEmailFromJwt(jwt));
    if (key) {
      localStorage.setItem(
        key,
        JSON.stringify({ permissions, profiles: data?.profiles ?? [] })
      );
    }
    return permissions;
  } catch (error) {
    console.error("Não foi possível carregar as permissões no login.", error);
    return [];
  }
};

/** Limpa o cache de permissões de todos os usuários — chamado no logout. */
export const clearPermissionCache = () => {
  Object.keys(localStorage)
    .filter((k) => k.startsWith(CACHE_PREFIX))
    .forEach((k) => localStorage.removeItem(k));
};
